import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18n from "../i18n";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const paymentSlice = createApi({
  reducerPath: "paymentSlice",
  baseQuery,
  tagTypes: ["payment"],
  endpoints: (builder) => ({
    getPaymentPlan: builder.query({
      query: ({ page, limit }) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        return `subscription-plans?${params.toString()}`;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const plan = data?.data?.plans ?? [];
          if (!Array.isArray(plan) || plan.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const subscriptionPlan of plan) {
            if (subscriptionPlan.user_type)
              bucket.push(subscriptionPlan.user_type);
            if (subscriptionPlan.name) bucket.push(subscriptionPlan.name);
            if (subscriptionPlan.description)
              bucket.push(subscriptionPlan.description);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            paymentSlice.util.updateQueryData(
              "getPaymentPlan",
              arg,
              (draft) => {
                const arr = draft?.data?.plans ?? [];
                arr.forEach((subscriptionPlans) => {
                  subscriptionPlans.user_type = pick(
                    subscriptionPlans.user_type,
                  );
                  subscriptionPlans.name = pick(subscriptionPlans.name);
                  subscriptionPlans.description = pick(
                    subscriptionPlans.description,
                  );
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["payment"],
    }),

    createPaymentPlan: builder.mutation({
      query: (data) => ({
        url: "subscription-plans",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    createSubscriptionPayment: builder.mutation({
      query: (data) => ({
        url: "subscription-payment",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    createFreeSubscriptionPayment: builder.mutation({
      query: (data) => ({
        url: "subscription-payment/free",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    paymentComplete: builder.mutation({
      query: (data) => ({
        url: "subscription-payment/complete",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    updatePaymentPlan: builder.mutation({
      query: ({ id, data }) => ({
        url: `subscription-plans/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    changeStatus: builder.mutation({
      query: (id) => ({
        url: `subscription-plans/${id}/toggle-status`,
        method: "PATCH",
      }),
      invalidatesTags: ["payment"],
    }),

    cancelPaymentPlan: builder.mutation({
      query: ({ id, data }) => ({
        url: `subscription-payment/${id}/cancel`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
    }),

    deletePaymentPlan: builder.mutation({
      query: (id) => ({
        url: `subscription-plans/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["payment"],
    }),
  }),
});

export const {
  useGetPaymentPlanQuery, // old use
  useCreatePaymentPlanMutation, // old use
  useCreateSubscriptionPaymentMutation,
  useCreateFreeSubscriptionPaymentMutation,
  usePaymentCompleteMutation,
  useUpdatePaymentPlanMutation,
  useCancelPaymentPlanMutation,
  useChangeStatusMutation,
  useDeletePaymentPlanMutation,
} = paymentSlice;
